import { cn } from "../utils/cn";
import { formatCurrency } from "../utils/formatters";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PriceBadgeProps {
  /** Negative = cheaper than average, positive = more expensive */
  priceVsAvg: number;
  size?: "sm" | "md";
  className?: string;
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function ArrowDownIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M12 5v14M5 12l7 7 7-7" />
    </svg>
  );
}

function ArrowUpIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

// ── Size tokens ───────────────────────────────────────────────────────────────

const SIZE = {
  sm: { pill: "px-2 py-0.5 text-[11px] gap-1 rounded-md", icon: 10 },
  md: { pill: "px-2.5 py-1 text-xs gap-1.5 rounded-lg",   icon: 12 },
};

// ── Component ─────────────────────────────────────────────────────────────────

export function PriceBadge({ priceVsAvg, size = "sm", className }: PriceBadgeProps) {
  const { pill, icon } = SIZE[size];
  const abs = Math.abs(priceVsAvg);

  if (priceVsAvg < 0) {
    // Cheaper than average
    return (
      <span
        className={cn(
          "inline-flex items-center font-semibold border",
          "bg-green-50 text-green-700 border-green-200",
          "dark:bg-green-900/20 dark:text-green-400 dark:border-green-800/50",
          pill, className,
        )}
      >
        <ArrowDownIcon size={icon} />
        {formatCurrency(abs)} below average
      </span>
    );
  }

  if (priceVsAvg > 0) {
    // More expensive than average
    return (
      <span
        className={cn(
          "inline-flex items-center font-semibold border",
          "bg-amber-50 text-amber-700 border-amber-200",
          "dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800/50",
          pill, className,
        )}
      >
        <ArrowUpIcon size={icon} />
        {formatCurrency(abs)} above average
      </span>
    );
  }

  // At average
  return (
    <span
      className={cn(
        "inline-flex items-center font-semibold border",
        "bg-surface-overlay text-content-secondary border-border-strong",
        pill, className,
      )}
    >
      At average price
    </span>
  );
}

PriceBadge.displayName = "PriceBadge";
